/**
 * Budowy otwartego dnia na karcie Mapa: zamowienia z listy tabeli zamienione na znaczniki
 * (jedno miejsce = jeden znacznik), z filtrem stanow i lista budow bez wspolrzednych.
 */

// Pola wiersza z /api/zamowienia, z ktorych korzysta mapa (wiersz tabeli ma ich wiecej).
export interface ZamowienieNaMapie {
  id: number;
  zamowienie_numer?: number;
  zamowienie_godzinaod?: string;
  zamowienie_godzinado?: string;
  zamowienie_ilosc?: number;
  klient_nazwa?: string;
  klient_budowa?: string;
  wezel_nazwa?: string;
  budowa_lat?: number | string | null;
  budowa_lon?: number | string | null;
  zamowienie_potwierdzone?: boolean;
  zamowienie_realizacja?: boolean;
  zamowienie_zakonczone?: boolean;
  zamowienie_anulowane?: boolean;
}

export type StanZamowienia = 'Nowe' | 'Potwierdzone' | 'Realizacja' | 'Zakonczone' | 'Anulowane';
export type StanFiltrowany = Exclude<StanZamowienia, 'Nowe'>;
export type FiltrStanow = Record<StanFiltrowany, boolean>;

// Anulowane domyslnie ukryte: to nie sa dostawy. Nowe (bez zadnej flagi) widac zawsze, jak w tabeli.
// Gdy na stronie jest tabela zamowien, kropki biora wartosci z jej filtra (FiltrStanowTabeli).
export const FILTR_DOMYSLNY: FiltrStanow = { Potwierdzone: true, Realizacja: true, Zakonczone: true, Anulowane: false };

// Filtr stanow tabeli zamowien: false = stan wylaczony, brak klucza = wlaczony.
export type FiltrStanowTabeli = { potwierdzone?: boolean; realizacja?: boolean; zakonczone?: boolean; anulowane?: boolean };

/** Ta sama kolejnosc co kolor wiersza w tabeli zamowien (flagi narastaja w cyklu zycia). */
export function stanZamowienia(z: ZamowienieNaMapie): StanZamowienia {
  if (z.zamowienie_anulowane) return 'Anulowane';
  if (z.zamowienie_zakonczone) return 'Zakonczone';
  if (z.zamowienie_realizacja) return 'Realizacja';
  if (z.zamowienie_potwierdzone) return 'Potwierdzone';
  return 'Nowe';
}

// Kolor budowy z kilkoma zamowieniami: najbardziej „zywy” stan sposrod widocznych.
const ZYWOSC: StanZamowienia[] = ['Realizacja', 'Potwierdzone', 'Nowe', 'Zakonczone', 'Anulowane'];

export interface BudowaNaMapie {
  klucz: string;
  lat: number;
  lng: number;
  nazwa: string;
  stan: StanZamowienia;
  zamowienia: ZamowienieNaMapie[];
}

export interface BudowyDnia {
  budowy: BudowaNaMapie[];
  bezLokalizacji: Array<{ nazwa: string; liczba: number }>;
  liczbyStanow: Record<StanZamowienia, number>;
  // Punkty wszystkich budow z lokalizacja, bez filtra: kadr mapy nie skacze przy klikaniu kropek.
  punktyWszystkich: Array<{ lat: number; lng: number }>;
}

const liczba = (v: unknown): number => (v === null || v === undefined || v === '' ? NaN : Number(v));

function wspolrzedne(z: ZamowienieNaMapie): { lat: number; lng: number } | null {
  const lat = liczba(z.budowa_lat);
  const lng = liczba(z.budowa_lon);
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return { lat, lng };
}

const nazwaBudowy = (z: ZamowienieNaMapie) =>
  [z.klient_budowa, z.klient_nazwa].map((s) => (s || '').trim()).filter(Boolean).join(' · ') || 'Budowa bez nazwy';

export function budowyDnia(zamowienia: ZamowienieNaMapie[], filtr: FiltrStanow): BudowyDnia {
  const liczbyStanow: Record<StanZamowienia, number> = { Nowe: 0, Potwierdzone: 0, Realizacja: 0, Zakonczone: 0, Anulowane: 0 };
  const budowy = new Map<string, BudowaNaMapie>();
  const bezLokalizacji = new Map<string, number>();
  const punkty = new Map<string, { lat: number; lng: number }>();

  for (const z of zamowienia) {
    const stan = stanZamowienia(z);
    liczbyStanow[stan] += 1;
    const punkt = wspolrzedne(z);
    // To samo miejsce (z dokladnoscia ~1 m) to jeden znacznik, nawet gdy nazwy budow sie roznia.
    const klucz = punkt ? `${punkt.lat.toFixed(5)},${punkt.lng.toFixed(5)}` : '';
    if (punkt) punkty.set(klucz, punkt);

    const widoczne = stan === 'Nowe' || filtr[stan];
    if (!widoczne) continue;
    if (!punkt) {
      const nazwa = nazwaBudowy(z);
      bezLokalizacji.set(nazwa, (bezLokalizacji.get(nazwa) || 0) + 1);
      continue;
    }
    const budowa = budowy.get(klucz);
    if (budowa) {
      budowa.zamowienia.push(z);
      if (ZYWOSC.indexOf(stan) < ZYWOSC.indexOf(budowa.stan)) budowa.stan = stan;
    } else {
      budowy.set(klucz, { klucz, ...punkt, nazwa: nazwaBudowy(z), stan, zamowienia: [z] });
    }
  }

  for (const b of budowy.values()) {
    b.zamowienia.sort((a, c) => String(a.zamowienie_godzinaod || '').localeCompare(String(c.zamowienie_godzinaod || '')));
  }

  return {
    budowy: [...budowy.values()],
    bezLokalizacji: [...bezLokalizacji.entries()]
      .map(([nazwa, n]) => ({ nazwa, liczba: n }))
      .sort((a, b) => a.nazwa.localeCompare(b.nazwa, 'pl')),
    liczbyStanow,
    punktyWszystkich: [...punkty.values()],
  };
}
